import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Maximize, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { INSTAGRAM_URL } from '../../lib/api';
import { useLang } from '../../i18n';

const M = '/assets/company/miniserie';

/**
 * Miniserie dei 30 anni con Barbara Vitale (Instagram, 25/09–29/10/2026).
 * Video ricodificati in H.264 720×1280 dagli originali VP9; i sottotitoli
 * sono impressi nel video. Le domande sono quelle a schermo in ogni puntata.
 */
export const PUNTATE = [
  { n: 1, data: '2026-09-25', durata: 53, post: 'https://www.instagram.com/p/DdtZEvet0_H/' },
  { n: 2, data: '2026-10-06', durata: 53, post: 'https://www.instagram.com/p/DeJudEQN1Zo/' },
  { n: 3, data: '2026-10-15', durata: 39, post: 'https://www.instagram.com/p/Deg7AIauwvq/' },
  { n: 4, data: '2026-10-29', durata: 27, post: 'https://www.instagram.com/p/DfFDC_BtLcK/' },
].map((p) => ({ ...p, src: `${M}/puntata-${p.n}.mp4`, poster: `${M}/puntata-${p.n}-poster.jpg` }));

const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** VideoObject per il JSON-LD della pagina Azienda. */
export const puntateJsonLd = (t) =>
  PUNTATE.map((p, i) => ({
    '@type': 'VideoObject',
    name: `${t('company.seriesTitle')} · ${t('company.seriesEpisode')(p.n, PUNTATE.length)}`,
    description: t('company.seriesQuestions')[i],
    thumbnailUrl: p.poster,
    contentUrl: p.src,
    uploadDate: p.data,
    duration: `PT${p.durata}S`,
    inLanguage: 'it',
  }));

export function Miniserie() {
  const { t, lang } = useLang();
  const ridotto = useReducedMotion();
  const box = useRef(null);
  const video = useRef(null);
  const [attiva, setAttiva] = useState(0);
  const [avviata, setAvviata] = useState(false); // il primo play lo decide chi guarda
  const [inRiproduzione, setInRiproduzione] = useState(false);
  const [muto, setMuto] = useState(false);
  const [tempo, setTempo] = useState(0);
  const [durata, setDurata] = useState(PUNTATE[0].durata);
  const domande = t('company.seriesQuestions');
  const p = PUNTATE[attiva];
  const data = new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'it-IT', { day: 'numeric', month: 'long' });

  // fuori dallo schermo si ferma: niente audio che parla da una sezione che non si vede
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) video.current?.pause?.();
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const suona = () => video.current?.play?.()?.catch?.(() => {});

  const scegli = (i) => {
    setAvviata(true);
    if (i === attiva) {
      suona();
      return;
    }
    setAttiva(i);
    setTempo(0);
    setDurata(PUNTATE[i].durata);
    // sotto i 768px l'elenco sta sotto il player: riportalo in vista
    const r = box.current?.getBoundingClientRect?.();
    if (r && (r.top < 0 || r.bottom > window.innerHeight)) {
      box.current.scrollIntoView?.({ behavior: ridotto ? 'auto' : 'smooth', block: 'center' });
    }
  };

  // cambio puntata: il nuovo src parte subito se chi guarda l'ha gia' avviata
  useEffect(() => {
    setInRiproduzione(false);
    if (avviata) suona();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attiva]);

  const comando = () => {
    setAvviata(true);
    if (inRiproduzione) video.current.pause();
    else suona();
  };

  const schermoIntero = () => {
    const v = video.current;
    if (v?.requestFullscreen) v.requestFullscreen().catch(() => {});
    else v?.webkitEnterFullscreen?.(); // iOS Safari
  };

  const avanzamento = durata ? Math.min(tempo / durata, 1) : 0;

  return (
    <section
      data-testid="company-series"
      aria-labelledby="company-series-title"
      className="relative overflow-hidden bg-notte pb-20 text-carta md:pb-32"
    >
      <div className="wrap">
        <div className="border-t border-carta/15 pt-14 md:pt-20">
          <p className="accento text-xl text-ocra md:text-2xl">{t('company.seriesKicker')}</p>
          <h2
            id="company-series-title"
            className="mt-3 max-w-[18ch] text-[clamp(2rem,5vw,3.6rem)] leading-[1.04]"
          >
            {t('company.seriesTitle')}
          </h2>
          <p className="prosa mt-5 max-w-[56ch] text-carta/75">{t('company.seriesLead')}</p>
        </div>

        <div className="mt-12 grid gap-10 md:mt-16 md:grid-cols-12 md:gap-12 lg:gap-16">
          {/* ─── il player: verticale come è nato, controlli nostri ─── */}
          <div ref={box} className="md:col-span-5 lg:col-span-4">
            <div className="relative mx-auto aspect-[9/16] max-h-[82svh] w-full max-w-[420px] overflow-hidden bg-inchiostro shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
              <video
                key={p.src}
                ref={video}
                src={p.src}
                poster={p.poster}
                width={720}
                height={1280}
                playsInline
                preload="none"
                muted={muto}
                aria-label={t('company.seriesVideoAlt')(p.n)}
                onClick={comando}
                onPlay={() => setInRiproduzione(true)}
                onPause={() => setInRiproduzione(false)}
                onTimeUpdate={(e) => setTempo(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => Number.isFinite(e.currentTarget.duration) && setDurata(e.currentTarget.duration)}
                onEnded={() => attiva < PUNTATE.length - 1 && setAttiva(attiva + 1)}
                className="absolute inset-0 h-full w-full cursor-pointer object-cover"
              />

              {/* prima del play: velo, numero della puntata e un solo invito */}
              {!inRiproduzione && tempo === 0 && (
                <button
                  type="button"
                  onClick={comando}
                  aria-label={t('company.seriesPlay')(p.n)}
                  className="group absolute inset-0 flex flex-col justify-between bg-gradient-to-t from-notte/85 via-notte/10 to-notte/40 p-6 text-left"
                >
                  <span className="font-display text-[clamp(4.5rem,9vw,7rem)] leading-none text-carta/90">
                    {String(p.n).padStart(2, '0')}
                  </span>
                  <span className="flex items-center gap-4">
                    <span className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-carta text-inchiostro transition-transform duration-300 group-hover:scale-105">
                      <Play size={22} strokeWidth={2} className="translate-x-[2px]" />
                    </span>
                    <span className="text-[14px] font-bold">
                      {t('company.seriesPlay')(p.n)}
                      <span className="block font-normal text-carta/70">{mmss(p.durata)}</span>
                    </span>
                  </span>
                </button>
              )}

              {/* barra comandi, visibile appena la puntata e' partita */}
              {(inRiproduzione || tempo > 0) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-notte/90 to-transparent px-4 pb-3 pt-10">
                  <input
                    type="range"
                    min={0}
                    max={durata || 0}
                    step={0.1}
                    value={tempo}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      video.current.currentTime = v;
                      setTempo(v);
                    }}
                    aria-label={t('company.seriesSeek')}
                    aria-valuetext={`${mmss(tempo)} / ${mmss(durata)}`}
                    className="serie-barra w-full"
                    style={{ '--avanzamento': `${avanzamento * 100}%` }}
                  />
                  <div className="mt-1 flex items-center gap-1 text-[13px] tabular-nums text-carta/80">
                    <button
                      type="button"
                      onClick={comando}
                      aria-label={t(inRiproduzione ? 'common.pauseVideo' : 'common.playVideo')}
                      className="grid h-10 w-10 place-items-center rounded-full hover:bg-carta/10"
                    >
                      {inRiproduzione ? <Pause size={18} strokeWidth={2} /> : <Play size={18} strokeWidth={2} />}
                    </button>
                    <span className="mr-auto">
                      {mmss(tempo)} / {mmss(durata)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setMuto((m) => !m)}
                      aria-label={t(muto ? 'company.seriesUnmute' : 'company.seriesMute')}
                      className="grid h-10 w-10 place-items-center rounded-full hover:bg-carta/10"
                    >
                      {muto ? <VolumeX size={18} strokeWidth={2} /> : <Volume2 size={18} strokeWidth={2} />}
                    </button>
                    <button
                      type="button"
                      onClick={schermoIntero}
                      aria-label={t('company.seriesFullscreen')}
                      className="grid h-10 w-10 place-items-center rounded-full hover:bg-carta/10"
                    >
                      <Maximize size={17} strokeWidth={2} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ─── le quattro domande: l'indice e' la serie ─── */}
          <ol className="md:col-span-7 lg:col-span-8 md:self-center">
            {PUNTATE.map((x, i) => {
              const corrente = i === attiva;
              return (
                <li key={x.n} className="border-t border-carta/15 last:border-b">
                  <button
                    type="button"
                    onClick={() => scegli(i)}
                    aria-current={corrente ? 'true' : undefined}
                    data-testid={`company-series-ep-${x.n}`}
                    className={`group relative grid w-full grid-cols-[3.25rem_1fr] items-baseline gap-x-4 py-6 text-left transition-colors duration-300 md:grid-cols-[5rem_1fr] md:py-8 ${
                      corrente ? 'text-carta' : 'text-carta/45 hover:text-carta/85'
                    }`}
                  >
                    <span
                      className={`font-display text-[clamp(1.75rem,3vw,2.6rem)] leading-none transition-colors ${
                        corrente ? 'text-ocra' : ''
                      }`}
                    >
                      {String(x.n).padStart(2, '0')}
                    </span>
                    <span>
                      <span className="block font-display text-[clamp(1.35rem,2.6vw,2.25rem)] italic leading-[1.12]">
                        {domande[i]}
                      </span>
                      <span className="mt-3 flex flex-wrap gap-x-3 text-[13px] font-semibold not-italic text-carta/55">
                        <span>{t('company.seriesEpisode')(x.n, PUNTATE.length)}</span>
                        <span aria-hidden>·</span>
                        <time dateTime={x.data}>{data.format(new Date(`${x.data}T12:00:00`))}</time>
                        <span aria-hidden>·</span>
                        <span>{mmss(x.durata)}</span>
                      </span>
                    </span>
                    {/* il filo dell'avanzamento corre sotto la puntata in onda */}
                    {corrente && (
                      <span
                        aria-hidden
                        className="absolute bottom-[-1px] left-0 h-[2px] w-full origin-left bg-ocra"
                        style={{ transform: `scaleX(${avanzamento})` }}
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 md:mt-16">
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="btn btn-line-light">
            {t('company.seriesInstagram')}
            <ArrowUpRight size={15} strokeWidth={2} />
          </a>
          <a
            href={p.post}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] font-semibold text-carta/60 underline-offset-4 hover:text-carta hover:underline"
          >
            {t('company.seriesOnInstagram')(p.n)}
          </a>
        </div>
      </div>
    </section>
  );
}
